import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';

// The frame around every marketing page (homepage, /download, /install, the
// legal pages): shared header and footer, the always-light theme, and the
// scroll handling the router doesn't do for us.
//
// Pages rendered here are also prerendered to static HTML at build time
// (src/prerender.js), so nothing in this tree may touch window/document
// at render time or import LayoutContext (it reads the viewport at module
// scope). Effects are fine — they don't run in renderToStaticMarkup.
//
// `background` is an optional element painted under the content (the
// homepage's drifting blobs); it must position itself absolutely.
function MarketingLayout({ children, background = null, className = '' }) {
  const { setThemeOverride } = useTheme();
  const { pathname, hash } = useLocation();

  // The marketing pages are always light — the dark palette is tuned for
  // the logged-in app. The provider owns class application, so pin the
  // override while mounted and let unmount restore the user's theme.
  // public/index.html's boot script skips the dark pre-apply on these paths
  // for the same reason.
  useEffect(() => {
    setThemeOverride('light');
    return () => setThemeOverride(null);
  }, [setThemeOverride]);

  // #root is the scroll container (src/index.css), so the browser's own
  // fragment jump and scroll restoration never fire: land on the hash
  // target when there is one, otherwise start each page at the top.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const id = hash ? decodeURIComponent(hash.slice(1)) : '';
    const target = id ? document.getElementById(id) : null;
    if (target) {
      target.scrollIntoView();
      return;
    }
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, [pathname, hash]);

  return (
    <div className={`min-h-screen bg-[var(--color-paper-bg)] text-[var(--color-ink-main)] relative overflow-hidden selection:bg-[var(--color-accent-blue)] selection:text-[var(--color-ink-main)] ${className}`}>
      {background}
      <div className="relative z-10">
        <SiteHeader />
        {children}
        <SiteFooter />
      </div>
    </div>
  );
}

export default MarketingLayout;
